import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CreditCard, Key, Settings, CheckCircle, ArrowRight,
  HelpCircle, Shield, TrendingUp, AlertCircle, Loader2
} from 'lucide-react';
import { userService } from '../services/api';
import { getErrorMessage } from '../utils/helpers';
import toast from 'react-hot-toast';
import KisApiGuideModal from '../components/KisApiGuideModal';

const OnboardingPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showKisGuideModal, setShowKisGuideModal] = useState(false);

  // Step 1: 계좌번호
  const [accountNumber, setAccountNumber] = useState('');
  const [accountNumberError, setAccountNumberError] = useState('');

  // Step 2: API 키
  const [appKey, setAppKey] = useState('');
  const [appSecret, setAppSecret] = useState('');
  const [apiKeyError, setApiKeyError] = useState('');

  // Step 3: 투자 설정
  const [investmentSettings, setInvestmentSettings] = useState({
    ai_investment_limit: 1000000,
    auto_trading_enabled: true,
    trading_slots: {
      slot_0930: true,
      slot_1300: true,
      slot_1430: true,
      friday_sell: true
    }
  });

  // 계좌번호 형식 검증 (8자리-2자리)
  const validateAccountNumber = (value) => {
    const pattern = /^\d{8}-\d{2}$/;
    return pattern.test(value);
  };

  // Step 1: 계좌번호 저장
  const handleAccountNumberSubmit = () => {
    if (!accountNumber.trim()) {
      setAccountNumberError('계좌번호를 입력해주세요');
      return;
    }
    if (!validateAccountNumber(accountNumber)) {
      setAccountNumberError('계좌번호 형식이 올바르지 않습니다 (예: 12345678-01)');
      return;
    }
    setAccountNumberError('');
    setCurrentStep(2);
  };

  // Step 2: API 키 저장
  const handleApiKeysSubmit = async () => {
    if (!appKey.trim() || !appSecret.trim()) {
      setApiKeyError('App Key와 App Secret을 모두 입력해주세요');
      return;
    }

    setIsSaving(true);
    setApiKeyError('');

    try {
      // API 키 저장
      await userService.saveApiKeys({
        api_name: 'kis',
        kis_app_key: appKey,
        kis_app_secret: appSecret,
        kis_account_number: accountNumber
      });

      toast.success('API 키가 저장되었습니다');
      setCurrentStep(3);
    } catch (error) {
      setApiKeyError(getErrorMessage(error));
      toast.error('API 키 저장에 실패했습니다');
    } finally {
      setIsSaving(false);
    }
  };

  // Step 3: 투자 설정 저장 및 완료
  const handleSettingsSubmit = async () => {
    setIsSaving(true);

    try {
      await userService.saveAiInvestmentSettings(investmentSettings);
      toast.success('설정이 완료되었습니다! 이제 AI 트레이딩을 시작할 수 있습니다.');

      // 온보딩 완료 후 대시보드로 이동
      navigate('/account');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  // 단계 표시 컴포넌트
  const StepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3].map((step) => (
        <React.Fragment key={step}>
          <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all ${
            currentStep >= step
              ? 'bg-blue-600 border-blue-600 text-white'
              : 'border-gray-300 text-gray-400'
          }`}>
            {currentStep > step ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <span className="font-semibold">{step}</span>
            )}
          </div>
          {step < 3 && (
            <div className={`w-16 h-1 mx-2 rounded ${
              currentStep > step ? 'bg-blue-600' : 'bg-gray-200'
            }`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* 헤더 */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4">
            <TrendingUp className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">AI Trading 설정</h1>
          <p className="text-gray-600 mt-2">서비스를 이용하기 위한 초기 설정을 진행합니다</p>
        </div>

        {/* 단계 표시 */}
        <StepIndicator />

        {/* 카드 */}
        <div className="bg-white rounded-2xl shadow-xl p-8">

          {/* Step 1: 계좌번호 입력 */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mb-4">
                  <CreditCard className="w-6 h-6 text-blue-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">계좌번호 등록</h2>
                <p className="text-gray-600 mt-2">한국투자증권 계좌번호를 입력해주세요</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  계좌번호 <span className="text-gray-400">(8자리-2자리)</span>
                </label>
                <input
                  type="text"
                  value={accountNumber}
                  onChange={(e) => {
                    setAccountNumber(e.target.value);
                    setAccountNumberError('');
                  }}
                  placeholder="12345678-01"
                  className={`w-full px-4 py-3 border rounded-lg text-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    accountNumberError ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {accountNumberError && (
                  <p className="text-red-500 text-sm mt-2 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {accountNumberError}
                  </p>
                )}
              </div>

              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-start">
                  <HelpCircle className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium">계좌번호 확인 방법</p>
                    <p className="mt-1">한국투자증권 앱 → 메뉴 → 계좌관리에서 확인 가능합니다.</p>
                  </div>
                </div>
              </div>

              <button
                onClick={handleAccountNumberSubmit}
                className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center"
              >
                다음 단계
                <ArrowRight className="w-5 h-5 ml-2" />
              </button>
            </div>
          )}

          {/* Step 2: API 키 입력 */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mb-4">
                  <Key className="w-6 h-6 text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">API 키 등록</h2>
                <p className="text-gray-600 mt-2">KIS Open API 키를 입력해주세요</p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">App Key</label>
                  <input
                    type="text"
                    value={appKey}
                    onChange={(e) => {
                      setAppKey(e.target.value);
                      setApiKeyError('');
                    }}
                    placeholder="App Key 입력"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">App Secret</label>
                  <input
                    type="password"
                    value={appSecret}
                    onChange={(e) => {
                      setAppSecret(e.target.value);
                      setApiKeyError('');
                    }}
                    placeholder="App Secret 입력"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {apiKeyError && (
                <p className="text-red-500 text-sm flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {apiKeyError}
                </p>
              )}

              <button
                onClick={() => setShowKisGuideModal(true)}
                className="w-full py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors flex items-center justify-center"
              >
                <HelpCircle className="w-5 h-5 mr-2" />
                API 키 발급 방법 보기
              </button>

              <div className="flex space-x-3">
                <button
                  onClick={() => setCurrentStep(1)}
                  className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                >
                  이전
                </button>
                <button
                  onClick={handleApiKeysSubmit}
                  disabled={isSaving}
                  className="flex-1 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center justify-center"
                >
                  {isSaving ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      다음 단계
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: 투자 설정 */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-purple-100 rounded-full mb-4">
                  <Settings className="w-6 h-6 text-purple-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">투자 설정</h2>
                <p className="text-gray-600 mt-2">AI 자동매매 설정을 완료해주세요</p>
              </div>

              {/* 투자 금액 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  AI 투자 한도 금액 (원)
                </label>
                <input
                  type="number"
                  value={investmentSettings.ai_investment_limit}
                  onChange={(e) => setInvestmentSettings(prev => ({
                    ...prev,
                    ai_investment_limit: parseInt(e.target.value) || 0
                  }))}
                  placeholder="1000000"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-sm text-gray-500 mt-1">
                  이 금액 범위 내에서만 AI가 자동으로 매매합니다
                </p>
              </div>

              {/* 자동매매 활성화 */}
              <div className="bg-yellow-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">AI 자동매매</p>
                    <p className="text-sm text-gray-600">AI가 자동으로 주식을 매수/매도합니다</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={investmentSettings.auto_trading_enabled}
                      onChange={(e) => setInvestmentSettings(prev => ({
                        ...prev,
                        auto_trading_enabled: e.target.checked
                      }))}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                  </label>
                </div>
              </div>

              {/* 매매 시간대 선택 */}
              <div className="space-y-3">
                <p className="text-sm font-medium text-gray-700">매매 시간대 선택</p>

                {/* 오전 세션 */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900 text-sm">오전 세션 (09:30)</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={investmentSettings.trading_slots.slot_0930}
                      onChange={(e) => setInvestmentSettings(prev => ({
                        ...prev,
                        trading_slots: { ...prev.trading_slots, slot_0930: e.target.checked }
                      }))}
                    />
                    <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-purple-600"></div>
                  </label>
                </div>

                {/* 오후 세션 */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900 text-sm">오후 세션 (13:00)</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={investmentSettings.trading_slots.slot_1300}
                      onChange={(e) => setInvestmentSettings(prev => ({
                        ...prev,
                        trading_slots: { ...prev.trading_slots, slot_1300: e.target.checked }
                      }))}
                    />
                    <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-purple-600"></div>
                  </label>
                </div>

                {/* 마감 세션 */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900 text-sm">마감 세션 (14:30)</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={investmentSettings.trading_slots.slot_1430}
                      onChange={(e) => setInvestmentSettings(prev => ({
                        ...prev,
                        trading_slots: { ...prev.trading_slots, slot_1430: e.target.checked }
                      }))}
                    />
                    <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-purple-600"></div>
                  </label>
                </div>

                {/* 금요일 전량 매도 */}
                <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100">
                  <div>
                    <p className="font-medium text-gray-900 text-sm">금요일 전량 매도 (15:20)</p>
                    <p className="text-xs text-gray-500">블랙 먼데이 회피용</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={investmentSettings.trading_slots.friday_sell}
                      onChange={(e) => setInvestmentSettings(prev => ({
                        ...prev,
                        trading_slots: { ...prev.trading_slots, friday_sell: e.target.checked }
                      }))}
                    />
                    <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-red-600"></div>
                  </label>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setCurrentStep(2)}
                  className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                >
                  이전
                </button>
                <button
                  onClick={handleSettingsSubmit}
                  disabled={isSaving}
                  className="flex-1 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center justify-center"
                >
                  {isSaving ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5 mr-2" />
                      설정 완료
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* 보안 안내 */}
        <div className="mt-6 text-center">
          <div className="inline-flex items-center text-sm text-gray-500">
            <Shield className="w-4 h-4 mr-2" />
            모든 API 키는 암호화되어 안전하게 저장됩니다
          </div>
        </div>
      </div>

      {/* KIS API 가이드 모달 */}
      <KisApiGuideModal
        isOpen={showKisGuideModal}
        onClose={() => setShowKisGuideModal(false)}
      />
    </div>
  );
};

export default OnboardingPage;
